import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
} from '@mui/material';
import { Campaign } from '@internal/backstage-plugin-bidone-backend';
import { useEndCampaign } from '../../hooks/useCampaigns';

type EndCampaignDialogProps = {
  open: boolean;
  campaign: Campaign;
  onClose: () => void;
};

// End a campaign (terminal). Checks copied from the rubric default to
// migrating back; campaign-defined checks can optionally be promoted. The
// selection is recorded now — the migration mechanics land in Phase 6.
export const EndCampaignDialog = ({
  open,
  campaign,
  onClose,
}: EndCampaignDialogProps) => {
  const endCampaign = useEndCampaign();
  const [migrate, setMigrate] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setMigrate(
        new Set(
          campaign.checks
            .filter(c => c.origin === 'copied-from-rubric')
            .map(c => c.id),
        ),
      );
      endCampaign.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaign]);

  const toggle = (id: string) =>
    setMigrate(existing => {
      const next = new Set(existing);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const handleEnd = async () => {
    await endCampaign.mutateAsync({
      id: campaign.id,
      migratedCheckIds: Array.from(migrate),
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>End campaign</DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          Ending is permanent — the campaign becomes read-only. Choose which
          checks should move (back) to the rubric:
        </Typography>
        {endCampaign.error ? (
          <Alert severity="error">{(endCampaign.error as Error).message}</Alert>
        ) : null}
        <List dense>
          {campaign.checks.map(check => (
            <ListItemButton key={check.id} onClick={() => toggle(check.id)}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  size="small"
                  checked={migrate.has(check.id)}
                  tabIndex={-1}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText
                primary={check.name}
                secondary={
                  check.origin === 'copied-from-rubric'
                    ? 'Copied from rubric — returns to the rubric'
                    : 'Campaign-defined — promote to the rubric'
                }
              />
            </ListItemButton>
          ))}
        </List>
        {campaign.checks.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            This campaign has no checks.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={endCampaign.isPending}
          onClick={() => {
            handleEnd().catch(() => undefined);
          }}
        >
          {endCampaign.isPending ? 'Ending…' : 'End campaign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
